"use client";

import React from "react";
import { Phone, Clock, MapPin, ShieldCheck, CircleAlert as AlertCircle, FileText } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  icon: React.ElementType;
  question: string;
  answer: string;
}
const FAQS: FaqItem[] = [{
  id: "item-1",
  icon: Clock,
  question: "What is your emergency response time in Anne Arundel County?",
  answer: "Our mobile restoration units deploy immediately with an average arrival window of approximately 60 minutes across Anne Arundel County. Because we operate fully equipped emergency vehicles stationed regionally rather than relying on a centralized dispatch depot, our technicians can reach your property quickly to begin water extraction, structural stabilization, and mitigation before secondary damage accelerates."
}, {
  id: "item-2",
  icon: MapPin,
  question: "Which areas of Anne Arundel County does TriServe serve?",
  answer: "We provide full disaster restoration throughout all incorporated and unincorporated communities across Anne Arundel County. Key service areas include Annapolis, Severna Park, Arnold, Pasadena, Glen Burnie, Crofton, Edgewater, Millersville, Gambrills, Odenton, and Deale. We routinely handle tidal flooding, severe storm surge, and moisture intrusion for waterfront homes along the Chesapeake Bay shoreline as well as properties bordering the Severn, South, and Magothy rivers."
}, {
  id: "item-3",
  icon: ShieldCheck,
  question: "How does TriServe coordinate with insurance adjusters during the claims process?",
  answer: "We document all physical damage using industry-standard line-item estimating software accepted by major property insurance carriers. From day one, our technicians capture moisture mapping logs, thermal imaging readings, and high-resolution photo evidence. We submit this documentation directly to your carrier's assigned adjuster and coordinate throughout the adjustment process to ensure scopes of work are clearly understood and covered work proceeds without delay."
}, {
  id: "item-4",
  icon: AlertCircle,
  question: "What immediate steps should Anne Arundel County residents take after water, fire, or storm damage?",
  answer: "Prioritize physical safety first. For water emergencies, immediately locate and turn off the main water shut-off valve if safe to access, and avoid walking through standing water near submerged electrical outlets or basement subpanels. For fire or smoke incidents, avoid entering impacted areas until emergency officials clear the structure. If conditions allow, take quick timestamped photos and videos of the damage for your insurance record, and call our 24/7 dispatch line right away."
}, {
  id: "item-5",
  icon: FileText,
  question: "Does TriServe have a physical walk-in office in Maryland?",
  answer: "No. TriServe operates strictly as a rapid mobile emergency response service throughout Maryland. We do not operate a retail or walk-in customer counter. By focusing entirely on fully stocked mobile field units loaded with industrial dehumidifiers, air movers, and containment equipment, our crews go directly to your residential or commercial property to resolve the disaster on-site."
}, {
  id: "item-6",
  icon: Phone,
  question: "How can I reach TriServe for 24/7 emergency response?",
  answer: "You can contact our 24/7 emergency dispatch desk directly at 855-435-6631 at any time of day, night, weekend, or holiday. Calls are answered directly by local operations personnel—not an overseas answering service—ensuring immediate assessment of your situation and prompt mobilization of a mitigation crew to your Anne Arundel County address."
}];
export default function AnneArundelFaq() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section id="annearundelfaq" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/30 bg-primary/5 rounded-full">
            Anne Arundel County Dispatch
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Anne Arundel County Restoration FAQs
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Essential information regarding our ~60-minute local emergency response,
            coastal service areas, insurance claim coordination, and on-site deployment protocols.
          </p>
        </div>

        {/* Accordion Q&A */}
        <div className="bg-card rounded-xl border border-border p-4 sm:p-6 lg:p-8 shadow-xs">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {FAQS.map((faq, index) => {
            const IconComponent = faq.icon;
            return <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border/80 rounded-lg px-4 sm:px-6 bg-background data-[state=open]:bg-muted/40 transition-colors">
                  <AccordionTrigger className="hover:no-underline py-5 text-left gap-4 font-semibold text-foreground text-base sm:text-lg">
                    <span className="flex items-center gap-3.5">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <IconComponent className="size-4" aria-hidden="true" />
                      </span>
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pl-0 sm:pl-11 pt-1 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </div>

        {/* Emergency Contact Strip */}
        <div className="mt-10 p-6 rounded-xl bg-muted/60 border border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <p className="font-semibold text-foreground text-base">
              Dealing with active property damage in Anne Arundel County?
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Local mobile teams are on standby 24 hours a day, 365 days a year.
            </p>
          </div>
          <Button asChild size="lg" className="w-full sm:w-auto shrink-0 shadow-sm">
            <a href="tel:8554356631" className="inline-flex items-center gap-2">
              <Phone className="size-4" />
              <span>Call 855-435-6631</span>
            </a>
          </Button>
        </div>
      </div>

      {/* JSON-LD FAQPage Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
    </section>;
}